import { memo, useEffect, useMemo } from 'react';
import { MessagesSquare } from 'lucide-react';
import { EmptyState, Skeleton } from '@cyphex/ui';
import type { Conversation, Message, MessageId, UserRef } from '@cyphex/types';
import { useVirtualTimeline } from '../../hooks/useVirtualTimeline';
import type { MessageActions } from '../../hooks/useMessageActions';
import type { MessageExtras, TimelineRow } from '../../types';
import { MessageBubble } from './MessageBubble';
import { DayDivider, SystemMessage, UnreadDivider } from './TimelineDividers';
import { JumpToLatest } from './ThreadBanners';
import { ReadReceiptRow } from './ReadReceiptRow';

interface MessageTimelineProps {
  rows: readonly TimelineRow[];
  conversation: Conversation | null;
  me: UserRef | null;
  actions: MessageActions;
  isLoading: boolean;
  hasOlder: boolean;
  isFetchingOlder: boolean;
  loadOlder: () => void;
  onReply: (message: Message) => void;
  onForward: (message: Message) => void;
  onEdit: (message: Message) => void;
  onAtBottomChange: (atBottom: boolean) => void;
  unreadCount: number;
  extras: MessageExtras;
  /** Permalink target: parks the list on this row. */
  anchorId: MessageId | null;
  /** Same row while the flash lasts. */
  highlightId: MessageId | null;
}

/**
 * The virtualised timeline. Only the visible window plus a small overscan is
 * in the DOM — a conversation with 50 000 messages renders the same number of
 * nodes as one with 20.
 */
export const MessageTimeline = memo(function MessageTimeline({
  rows,
  conversation,
  me,
  actions,
  isLoading,
  hasOlder,
  isFetchingOlder,
  loadOlder,
  onReply,
  onForward,
  onEdit,
  onAtBottomChange,
  unreadCount,
  extras,
  anchorId,
  highlightId,
}: MessageTimelineProps) {
  const { scrollRef, virtualizer, atBottom, scrollToBottom } = useVirtualTimeline({
    rows,
    hasOlder,
    isFetchingOlder,
    loadOlder,
    anchorId,
  });

  useEffect(() => onAtBottomChange(atBottom), [atBottom, onAtBottomChange]);

  /** Highest seq every other member has read — drives the single receipt row. */
  /**
   * WHO read it only matters when there is more than one candidate.
   *
   * In a two-person chat "Read by <the one other person>" restates what the tick
   * beside the timestamp already said, and it did it in a full-width row with a
   * facepile — the noisiest possible way to say "seen". Teams shows the receipt
   * on the message in a 1:1 and saves the faces for group chats, where the
   * question "who has seen this?" has a real answer. So does this now.
   */
  const showFacepile =
    conversation !== null &&
    (conversation.kind === 'group' || conversation.memberCount > 2);

  const receiptSeq = useMemo(() => {
    const others = (conversation?.members ?? []).filter((member) => member.user.id !== me?.id);
    if (others.length === 0) return 0;
    return Math.min(...others.map((member) => member.lastReadSeq ?? 0));
  }, [conversation?.members, me?.id]);

  if (isLoading) {
    return (
      /* Matches the loaded thread's rhythm — a skeleton that is airier than the
         content it stands in for makes the list visibly reflow on arrival. */
      <div className="min-h-0 flex-1 space-y-2.5 p-4">
        {[0, 1, 2, 3, 4].map((index) => (
          <div key={index} className="flex gap-2">
            <Skeleton shape="circle" className="h-8 w-8" />
            <div className="flex-1 space-y-1">
              <Skeleton shape="text" className="h-3 w-32" />
              <Skeleton shape="text" lines={2} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          icon={MessagesSquare}
          title="No messages yet"
          description="Say hello — this conversation is all yours."
          size="md"
        />
      </div>
    );
  }

  const items = virtualizer.getVirtualItems();

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        className="scrollbar-quiet min-h-0 flex-1 overflow-y-auto"
        role="log"
        aria-live="polite"
        aria-label="Message history"
      >
      {isFetchingOlder ? (
        <div className="p-3">
          <Skeleton shape="text" lines={2} />
        </div>
      ) : null}

        <div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
        {items.map((item) => {
          const row = rows[item.index];
          if (!row) return null;
          return (
            <div
              key={item.key}
              ref={virtualizer.measureElement}
              data-index={item.index}
              className="absolute inset-x-0 top-0"
              style={{ transform: `translateY(${item.start}px)` }}
            >
              {row.kind === 'day' ? <DayDivider date={row.date} /> : null}
              {row.kind === 'unread' ? <UnreadDivider count={row.count} /> : null}
              {row.kind === 'message' && row.message.kind === 'system' ? (
                <SystemMessage message={row.message} />
              ) : null}
              {row.kind === 'message' && row.message.kind !== 'system' ? (
                <>
                  <MessageBubble
                    message={row.message}
                    grouped={row.grouped}
                    isOwn={row.message.sender?.id === me?.id}
                    pending={row.pending}
                    groupEnd={row.groupEnd}
                    readByOthers={receiptSeq >= row.message.seq}
                    extras={extras}
                    highlighted={highlightId === row.message.id}
                    onReply={onReply}
                    onForward={onForward}
                    onEdit={onEdit}
                    onDelete={(message) => actions.remove(message.id)}
                    onPin={actions.togglePin}
                    onReact={actions.toggleReaction}
                    onRetry={actions.retry}
                    onSendAnyway={actions.sendAnyway}
                    onDiscard={actions.discard}
                  />
                  {row.groupEnd && receiptSeq === row.message.seq && showFacepile ? (
                    <ReadReceiptRow
                      readers={(conversation?.members ?? [])
                        .filter((member) => member.user.id !== me?.id)
                        .map((member) => member.user)}
                    />
                  ) : null}
                </>
              ) : null}
            </div>
          );
        })}
        </div>
      </div>

      <JumpToLatest
        visible={!atBottom}
        unread={unreadCount}
        onClick={() => scrollToBottom('smooth')}
      />
    </div>
  );
});
